import React, { useEffect, useRef, useCallback } from 'react';
import Chart from 'chart.js/auto';
import 'chartjs-adapter-luxon';
import { DateTime } from 'luxon';
import moment from 'moment-timezone';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { useSelector } from 'react-redux';
import zoomPlugin from 'chartjs-plugin-zoom';
import { getStartTime } from '@/utilities/helpers/commonHelper';

const WeightScaleChart = ({
  chartData,
  userTimezone = 'America/New_York',
  is24hoursformat = true,
  userDefinedMeasurement,
  insightLoading,
  getDateFormatByLocation,
  selectedRange = 'today',
}) => {
  const canvasRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const trans = useSelector(translator);

  const BAR_THICKNESS = 10;
  const MAX_BAR_THICKNESS = 30;

  const formatDate = getDateFormatByLocation(userTimezone);
  const tooltipFormatString = is24hoursformat
    ? `${formatDate} , HH:mm:ss`
    : `${formatDate} , hh:mm:ss a`;

  const isToday = String(selectedRange || '').toLowerCase() === 'today';

  // Display timezone information using a custom plugin
  const tzDisplayText = `${trans('TIMEZONE')}: ${userTimezone}`;

  const loadGraph = (chartJsData) => {
    const yValues = chartJsData.datasets
      .flatMap((ds) => ds.data.map((p) => p?.y))
      .filter((v) => typeof v === 'number' && !isNaN(v));

    const yMin = yValues.length ? Math.min(...yValues) : null;
    const yMax = yValues.length ? Math.max(...yValues) : null;
    const yPadding = 10;
    const yMinRoundedRaw = yMin != null ? Math.floor((yMin - yPadding) / 10) * 10 : null;
    const yMinRounded = yMinRoundedRaw != null ? Math.max(0, yMinRoundedRaw) : null;
    const yMaxRounded = yMax != null ? Math.ceil((yMax + yPadding) / 10) * 10 : null;

    const chartObj = {
      type: 'bar',
      data: chartJsData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { bottom: 42 } },
        interaction: { mode: 'nearest', axis: 'x' },
        plugins: {
          title: { display: false },
          tooltip: {
            filter: (tooltipItem) => {
              const value = tooltipItem.raw?.y ?? tooltipItem.raw;
              return typeof value === 'number' && !isNaN(value);
            },
            callbacks: {
              title: (tooltipItems) => {
                if (!tooltipItems || tooltipItems?.length === 0) return '';
                const idx = tooltipItems[0].dataIndex;
                const rawTimestamp = tooltipItems[0].chart.data.rawTimestamps[idx];
                return rawTimestamp
                  ? DateTime.fromJSDate(rawTimestamp)
                      .setZone(userTimezone)
                      .toFormat(tooltipFormatString)
                  : '';
              },
            },
          },
          datalabels: { display: false },
          timeZonePlugin: {
            text: tzDisplayText,
            font: '14px Arial',
            color: '#666',
          },
          zoom: {
            zoom: {
              wheel: { enabled: false }, // Zoom with mouse wheel - to be enabled later
              pinch: { enabled: false }, // Zoom with pinch gesture - to be enabled later
              mode: 'x', // Zoom direction: 'x', 'y', or 'xy'
            },
            pan: {
              enabled: false, // Enable panning
              mode: 'x', // Pan along X-axis
            },
            limits: {
              x: { min: 'original', max: 'original' },
            },
          },
        },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: isToday ? 'hour' : 'day',
              displayFormats: isToday
                ? { hour: is24hoursformat ? 'HH:mm' : 'hh:mm a' }
                : { day: getDateFormatByLocation(userTimezone) },
            },
            min: getStartTime({ selectedRange, userTimezone }),
            max: moment().tz(userTimezone).toDate(),
            title: { display: false },
            ticks: {
              callback: (value) => {
                const date = new Date(value);

                if (isToday) {
                  if (is24hoursformat) {
                    const time = moment.utc(date).tz(userTimezone).format('HH:mm');
                    if (time === '00:00') {
                      return '24:00';
                    }
                    return time;
                  }
                  return moment.utc(date).tz(userTimezone).format('ha');
                }

                return new Intl.DateTimeFormat('en', {
                  month: 'short',
                  day: 'numeric',
                }).format(date); // e.g. "Sep 1"
              },
            },
            grid: { display: true, drawOnChartArea: false },
            border: {
              color: '#656565',
              display: true,
              dash: [5, 5],
              dashOffset: 1,
            },
          },
          y: {
            type: 'linear',
            ticks: { autoSkip: true, precision: true },
            border: {
              color: '#656565',
              display: true,
              dash: [5, 5],
              dashOffset: 1,
            },
            grid: { color: '#989898', lineWidth: 1 },
            ...(yMinRounded != null && yMaxRounded != null
              ? { min: yMinRounded, max: yMaxRounded }
              : {}),
          },
        },
      },
    };

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    chartInstanceRef.current = new Chart(canvasRef.current, chartObj);
  };

  const getAggregatedData = useCallback(
    (chartDataFn) => {
      const dataArray = Array.isArray(chartDataFn)
        ? chartData.filter((item) => item !== null)
        : chartData
          ? [chartData]
          : [];

      const weightData = dataArray
        .map((item) => {
          const utcTimestamp = item?.utc_timestamp_inms;
          const timestamp = utcTimestamp
            ? DateTime.fromISO(utcTimestamp, { zone: 'utc' }).setZone(userTimezone).toJSDate()
            : null;
          const weightValue = item?.data?.weight;
          let weight = null;
          if (typeof weightValue === 'number') {
            weight = Number(weightValue.toFixed(1));
          } else if (typeof weightValue === 'string' && !isNaN(parseFloat(weightValue))) {
            weight = Number(parseFloat(weightValue).toFixed(1));
          }
          return { timestamp, weight };
        })
        .filter((item) => item.timestamp !== null && item.weight !== null)
        .sort((a, b) => a.timestamp - b.timestamp);

      let aggregatedData = [];

      aggregatedData = weightData.map((h) => {
        return {
          groupTimestamp: h.timestamp,
          actualTimestamp: h.timestamp,
          weight: h.weight,
        };
      });

      aggregatedData.sort((a, b) => a.groupTimestamp - b.groupTimestamp);

      const latestPerMinute = Object.values(
        aggregatedData.reduce((acc, item) => {
          const ts = new Date(item.actualTimestamp);
          const key = DateTime.fromJSDate(ts)
            .toUTC() // normalize timezone
            .toFormat('yyyy-MM-dd HH:mm');

          // keep latest for that minute
          if (!acc[key] || ts > new Date(acc[key].actualTimestamp)) {
            acc[key] = item;
          }

          return acc;
        }, {})
      );

      return latestPerMinute;
    },
    [chartData, userTimezone]
  );

  useEffect(() => {
    if (insightLoading) return;

    const timeZonePlugin = {
      id: 'timeZonePlugin',
      afterDatasetsDraw: (chart, args, options) => {
        const { ctx, chartArea } = chart;
        const xAxis = chart.scales.x;
        const xAxisBottom = xAxis ? xAxis.bottom : chartArea.bottom;
        ctx.save();
        ctx.font = options.font || '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = options.color || 'black';
        const text = options.text || '';
        ctx.fillText(text, (chartArea.left + chartArea.right) / 2, xAxisBottom + 20);
        ctx.restore();
      },
    };

    Chart.register(timeZonePlugin);
    Chart.register(zoomPlugin);

    const aggregatedData = getAggregatedData(chartData);

    const labels = aggregatedData.map((r) => r.groupTimestamp);
    // Build chart.js data
    const chartJsData = {
      labels,
      rawTimestamps: aggregatedData.map((r) => r.actualTimestamp),
      datasets: [
        {
          label: `Weight (${userDefinedMeasurement === 'metric' ? 'kgs' : 'lbs'})`,
          data: aggregatedData.map((r) => ({
            x: r.groupTimestamp,
            y: r.weight,
          })),
          barThickness: BAR_THICKNESS,
          maxBarThickness: MAX_BAR_THICKNESS,
        },
      ],
    };

    loadGraph(chartJsData);
  }, []);

  useEffect(() => {
    const aggregatedData = getAggregatedData(chartData);
    const labels = aggregatedData.map((r) => r.groupTimestamp);
    const rawTimestamps = aggregatedData.map((r) => r.actualTimestamp);
    const datasets = [
      {
        label: `Weight (${userDefinedMeasurement === 'metric' ? 'kgs' : 'lbs'})`,
        data: aggregatedData.map((r) => ({
          x: r.groupTimestamp,
          y: Number(r.weight),
        })),
        barThickness: BAR_THICKNESS, // each bar = 30px wide
        // optional:
        maxBarThickness: MAX_BAR_THICKNESS,
      },
    ];

    if (chartInstanceRef.current) {
      // ✅ update existing chart
      chartInstanceRef.current.destroy();
      const chartJsData = {
        labels,
        rawTimestamps,
        datasets,
      };

      loadGraph(chartJsData);
    } else {
      const chartJsData = {
        labels,
        rawTimestamps,
        datasets,
      };

      loadGraph(chartJsData);
    }
  }, [chartData]);

  return (
    <div className="chart-container">
      <canvas ref={canvasRef} className="chart-canvas" />
    </div>
  );
};

export default WeightScaleChart;
